import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Download } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/site";

export default function CI62DownloadModal({ open, onOpenChange, onCaptured }) {
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!email.trim() || !company.trim()) {
      toast.error("Please enter both your email and company name.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        name: company.trim(),
        email: email.trim(),
        company: company.trim(),
        service: "CI62 Info PDF Download",
        message: "Requested the color QC information PDF download from the /color page.",
      });
      sessionStorage.setItem("ci62_lead_captured", "1");
      setEmail("");
      setCompany("");
      onOpenChange(false);
      onCaptured();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-slate-300 text-slate-900 px-4 h-12 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 transition-colors placeholder:text-slate-400";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ci62-download-modal" className="sm:max-w-md rounded-none border-slate-200">
        <DialogHeader>
          <DialogTitle className="font-pub text-2xl font-semibold text-slate-900">
            Download the information
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Enter your details to download the color QC information as a PDF.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} noValidate className="space-y-4 mt-2">
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-slate-600 mb-2 font-medium">
              Work Email <span className="text-teal-700">*</span>
            </label>
            <input
              data-testid="ci62-lead-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              placeholder="you@company.com"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-slate-600 mb-2 font-medium">
              Company <span className="text-teal-700">*</span>
            </label>
            <input
              data-testid="ci62-lead-company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Company name"
              className={inputCls}
            />
          </div>

          <button
            type="submit"
            data-testid="ci62-lead-submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center bg-teal-700 hover:bg-teal-800 text-white h-12 text-sm font-medium tracking-wide transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 w-4 h-4 animate-spin" strokeWidth={2} />
                Preparing download...
              </>
            ) : (
              <>
                <Download className="mr-2 w-4 h-4" strokeWidth={1.75} />
                Download PDF
              </>
            )}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
